import { useEffect, useState } from "react";
import { GripVertical, Save, Loader2, Copy, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import RichEditor from "../components/RichEditor";

const uid = () => "sec-" + Math.random().toString(36).slice(2, 9);

export default function SectionsBuilder() {
  const [sections, setSections] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { api.get("/admin/site").then((r) => setSections(r.data?.sections || [])); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/site/sections", sections); toast.success("লেআউট সংরক্ষিত হয়েছে"); }
    catch { toast.error("ব্যর্থ"); }
    finally { setBusy(false); }
  };

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const arr = [...sections];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, moved);
    setSections(arr);
    setDragIdx(null);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const arr = [...sections];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSections(arr);
  };

  const update = (idx, patch) => {
    const arr = [...sections];
    arr[idx] = { ...arr[idx], ...patch };
    setSections(arr);
  };

  const duplicate = (idx) => {
    const arr = [...sections];
    const copy = { ...arr[idx], id: uid(), label: arr[idx].label + " (কপি)" };
    arr.splice(idx + 1, 0, copy);
    setSections(arr);
    toast.success("সেকশন কপি হয়েছে");
  };

  const remove = (idx) => {
    setSections(sections.filter((_, i) => i !== idx));
  };

  const addCustom = () => {
    setSections([...sections, { id: uid(), key: "custom", label: "কাস্টম সেকশন", enabled: true, heading: "নতুন সেকশন", html: "<p>এখানে আপনার লেখা লিখুন...</p>" }]);
    toast.success("কাস্টম সেকশন যোগ হয়েছে");
  };

  if (sections == null) return <div className="text-stone-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> লোড হচ্ছে...</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-2">
        <div><h1 className="font-heading text-2xl font-bold text-stone-800">পেজ বিল্ডার</h1><p className="text-sm text-stone-500 mt-1">সেকশন টেনে সাজান, দেখান/লুকান, কপি বা যোগ করুন</p></div>
        <button onClick={save} disabled={busy} data-testid="save-sections" className="inline-flex items-center gap-2 rounded-full bg-[var(--c-primary)] text-white px-5 py-2 text-sm font-medium hover:bg-[var(--c-secondary)] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} সংরক্ষণ
        </button>
      </div>

      <div className="space-y-2 mt-6">
        {sections.map((s, idx) => (
          <div
            key={s.id}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(idx)}
            data-testid={`section-row-${idx}`}
            className={`bg-white rounded-xl border p-3 ${dragIdx === idx ? "border-[var(--c-secondary)] opacity-60" : "border-stone-200"}`}
          >
            <div className="flex items-center gap-3">
              <GripVertical className="h-5 w-5 text-stone-300 cursor-grab" />
              <div className="flex-1">
                <input value={s.label} onChange={(e) => update(idx, { label: e.target.value })} className="font-medium text-stone-800 bg-transparent focus:outline-none w-full" />
                <div className="text-xs text-stone-400">{s.key === "custom" ? "কাস্টম" : s.key}</div>
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer text-sm text-stone-500">
                <input type="checkbox" checked={s.enabled} onChange={(e) => update(idx, { enabled: e.target.checked })} className="h-4 w-4 accent-[var(--c-primary)]" />
                {s.enabled ? "দৃশ্যমান" : "লুকানো"}
              </label>
              <div className="flex items-center gap-0.5">
                <button onClick={() => move(idx, -1)} className="p-1.5 text-stone-400 hover:text-stone-700"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(idx, 1)} className="p-1.5 text-stone-400 hover:text-stone-700"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => duplicate(idx)} title="কপি" className="p-1.5 text-stone-400 hover:text-stone-700"><Copy className="h-4 w-4" /></button>
                {s.key === "custom" && <button onClick={() => remove(idx)} className="p-1.5 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
            {s.key === "custom" && (
              <div className="mt-3 pl-8">
                <button onClick={() => setExpanded(expanded === idx ? null : idx)} className="text-sm text-[var(--c-secondary)] hover:underline">{expanded === idx ? "কনটেন্ট লুকান" : "কনটেন্ট সম্পাদনা"}</button>
                {expanded === idx && (
                  <div className="mt-3 space-y-3">
                    <input value={s.heading || ""} onChange={(e) => update(idx, { heading: e.target.value })} placeholder="সেকশন শিরোনাম" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" />
                    <RichEditor value={s.html || ""} onChange={(v) => update(idx, { html: v })} />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={addCustom} className="mt-4 inline-flex items-center gap-2 rounded-full border border-dashed border-stone-300 px-5 py-2.5 text-sm text-stone-600 hover:border-[var(--c-accent)] hover:text-[var(--c-primary)] transition-colors">
        <Plus className="h-4 w-4" /> কাস্টম সেকশন যোগ করুন
      </button>
    </div>
  );
}
